import React from 'react';

/**
 * Base skeleton component with shimmer animation
 */
export function Skeleton({ className = '', ...props }) {
  return (
    <div
      className={`
        bg-gradient-to-r from-secondary-200 via-secondary-100 to-secondary-200
        dark:from-secondary-700 dark:via-secondary-600 dark:to-secondary-700
        bg-[length:200%_100%]
        animate-shimmer
        rounded-lg
        ${className}
      `.replace(/\s+/g, ' ').trim()}
      {...props}
    />
  );
}

/**
 * Text skeleton for simulating text lines
 */
export function SkeletonText({ lines = 3, className = '' }) {
  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={`h-4 ${i === lines - 1 ? 'w-3/4' : 'w-full'}`}
        />
      ))}
    </div>
  );
}

/**
 * Card skeleton for loading content cards
 */
export function SkeletonCard({ className = '' }) {
  return (
    <div
      className={`
        bg-white dark:bg-secondary-800
        rounded-2xl shadow-card
        overflow-hidden
        ${className}
      `}
    >
      {/* Image skeleton */}
      <Skeleton className="w-full rounded-none" style={{ aspectRatio: '3/2' }} />

      {/* Content skeleton */}
      <div className="p-4 md:p-5">
        <Skeleton className="h-5 w-3/4 mb-3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

/**
 * Grid of card skeletons
 */
export function SkeletonCardGrid({ count = 8, className = '' }) {
  return (
    <div className={`grid-autofit ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

/**
 * Avatar skeleton
 */
export function SkeletonAvatar({ size = 'md', className = '' }) {
  const sizes = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };

  return (
    <Skeleton className={`rounded-full ${sizes[size]} ${className}`} />
  );
}

/**
 * Table row skeleton
 */
export function SkeletonTableRow({ columns = 4, className = '' }) {
  return (
    <tr className={className}>
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
}

/**
 * Table skeleton with multiple rows
 */
export function SkeletonTable({ rows = 5, columns = 4, className = '' }) {
  return (
    <div className={`overflow-x-auto rounded-xl border border-secondary-200 dark:border-secondary-700 ${className}`}>
      <table className="w-full">
        <thead className="bg-secondary-50 dark:bg-secondary-800">
          <tr>
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <Skeleton className="h-4 w-20" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-secondary-200 dark:divide-secondary-700">
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonTableRow key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Sidebar skeleton
 */
export function SkeletonSidebar({ className = '' }) {
  return (
    <div className={`space-y-4 p-4 ${className}`}>
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-5 w-3/4" />
          {i % 2 === 0 && (
            <div className="ps-4 space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Page skeleton with title and content grid
 */
export function SkeletonPage({ className = '' }) {
  return (
    <div className={`page-wrapper ${className}`}>
      {/* Breadcrumbs */}
      <div className="flex gap-2 mb-6">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-20" />
      </div>

      {/* Title */}
      <div className="toolbar-center">
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-32" />
      </div>

      {/* Content grid */}
      <SkeletonCardGrid count={8} />
    </div>
  );
}

export default Skeleton;